import { useCallback, useMemo } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { AddTwo, ChevronBackward } from '@nine-thirty-five/material-symbols-react/rounded'
import { taskPageItems, useGetTasksPage, type Task } from '@zendia/frontend-shared'
import { axiosRequest } from '../../../utilities/axiosClient'
import { ActionBar } from '../../../components/ActionBar'
import { ActionButton, FloatingActionButton } from '../../../components/Button'
import { DashboardTaskItem } from '../../../components/dashboard/DashboardTaskItem'
import { GroupedTaskList } from '../../../components/dashboard/GroupedTaskList'
import { TasksFilterMenu } from '../../../components/dashboard/TasksFilterMenu'
import { useTasksView } from '../../../components/dashboard/useTasksView'
import {
  matchesCompletedFilter,
  matchesTaskFilters,
} from '../../../components/dashboard/taskFilterTypes'
import { parseTasksPanelTab, type TasksPanelTab } from '../../../components/dashboard/tasksPanelTab'
import { useLoadMoreOnScroll } from '../../../hooks/useLoadMoreOnScroll'
import { useAllTasksFiltersStore } from '../../../components/Task/useAllTasksFiltersStore'

export const Route = createFileRoute('/_authenticated/tasks/')({
  component: AllTasksPage,
  // The dashboard Tasks panel group the user came from, so going back returns the dashboard to it.
  // Opening a task carries it along, so the way back through this page still ends there.
  validateSearch: (search): { dashboardTab?: TasksPanelTab } => ({
    dashboardTab: parseTasksPanelTab(search.dashboardTab),
  }),
})

/**
 * Every one of the user's tasks, reached from the dashboard Tasks panel's "View All Tasks". It
 * has the panel's filter menu, and its tasks are filtered, sorted and paged on the server,
 * loading the next page as the end of the list scrolls into view.
 */
function AllTasksPage() {
  const router = useRouter()
  const { dashboardTab } = Route.useSearch()
  const [view, onViewChange] = useTasksView()
  const {
    showCompleted,
    groupBy,
    sortBy,
    filters,
    setShowCompleted,
    setGroupBy,
    setSortBy,
    setFilters,
  } = useAllTasksFiltersStore()
  const isGrouped = groupBy !== 'none'

  const params = useMemo(
    () => ({
      sortBy,
      includeCompleted: showCompleted,
      labelIds: filters.labelIds,
      priorities: filters.priorities,
      projectIds: filters.projectIds,
    }),
    [sortBy, showCompleted, filters],
  )
  // Grouped, only this list's first page is loaded, for the total in the subtitle: the groups
  // page their own tasks, and adding up their counts would count a task with several labels in
  // each of them
  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useGetTasksPage(axiosRequest, params)

  // The server has already filtered these. Filtering again only matters for an optimistic update
  // - a task just completed, with Show Completed off - which leaves before the refetch confirms it.
  const tasks = useMemo(
    () =>
      taskPageItems(data).filter(
        (task) => matchesCompletedFilter(task, showCompleted) && matchesTaskFilters(task, filters),
      ),
    [data, showCompleted, filters],
  )
  const total = data?.pages[0]?.total

  const loadMoreRef = useLoadMoreOnScroll({ hasNextPage, isFetchingNextPage, fetchNextPage })

  const onBack = useCallback(() => {
    router.navigate({ to: '/dashboard', search: { tab: dashboardTab } })
  }, [router, dashboardTab])

  const onCreateTask = useCallback(() => {
    router.navigate({ to: '/tasks/new/form', search: { dashboardTab, fromAllTasks: true } })
  }, [router, dashboardTab])

  const condensed = view === 'condensed'

  const renderTaskItems = (list: Task[]) =>
    list.map((task) => (
      <DashboardTaskItem
        key={task.id}
        task={task}
        condensed={condensed}
        dashboardTab={dashboardTab}
        fromAllTasks
      />
    ))

  const renderTasks = () => {
    if (isGrouped) {
      return (
        <GroupedTaskList
          // A new grouping starts with every group closed again
          key={groupBy}
          params={params}
          groupBy={groupBy}
          condensed={condensed}
          emptyLabel="No tasks match these filters."
          dashboardTab={dashboardTab}
          fromAllTasks
        />
      )
    }
    if (isLoading) {
      return (
        <div className="text-center py-8" style={{ color: 'var(--color-body-text)' }}>
          Loading tasks...
        </div>
      )
    }
    if (isError) {
      return (
        <div className="text-center py-8" style={{ color: 'var(--color-body-text)' }}>
          Unable to load tasks right now.
        </div>
      )
    }
    if (tasks.length === 0) {
      return (
        <div className="text-center py-8" style={{ color: 'var(--color-body-text)' }}>
          No tasks match these filters.
        </div>
      )
    }

    return <div className={condensed ? 'space-y-2' : 'space-y-4'}>{renderTaskItems(tasks)}</div>
  }

  return (
    <div className="flex flex-col p-4">
      <ActionBar
        startAction={
          <ActionButton
            onClick={onBack}
            label="Back to the dashboard"
            icon={<ChevronBackward className="size-6" />}
          />
        }
        title="All Tasks"
        subtitle={total !== undefined ? `${total} task${total !== 1 ? 's' : ''}` : undefined}
      />

      <section
        className="rounded-xl p-6"
        style={{
          backgroundColor: 'var(--color-card-background)',
          boxShadow: `0 4px 6px -1px var(--color-shadow-light), 0 2px 4px -1px var(--color-shadow-light)`,
        }}
      >
        <div className="flex justify-end mb-4">
          <TasksFilterMenu
            view={view}
            onViewChange={onViewChange}
            showCompleted={showCompleted}
            onShowCompletedChange={setShowCompleted}
            groupBy={groupBy}
            onGroupByChange={setGroupBy}
            sortBy={sortBy}
            onSortByChange={setSortBy}
            filters={filters}
            onFiltersChange={setFilters}
          />
        </div>

        {renderTasks()}

        {/* Grouped, each group loads its own tasks with Load More instead */}
        {!isGrouped && <div ref={loadMoreRef} aria-hidden="true" />}
        {!isGrouped && isFetchingNextPage && (
          <div className="text-center py-4 text-sm" style={{ color: 'var(--color-body-text)' }}>
            Loading more tasks...
          </div>
        )}
      </section>

      <FloatingActionButton
        onClick={onCreateTask}
        aria-label="Add a task"
        className="bottom-22 sm:bottom-10 right-4 sm:right-10"
      >
        <AddTwo />
      </FloatingActionButton>
    </div>
  )
}
